/**
 * Per-connector setup dialog (web). Opens when a connector card is clicked and
 * gives a real, client-specific "here's exactly how to connect" experience:
 *   • AI-rail (ChatGPT / Claude / Gemini / Grok / Perplexity): numbered
 *     steps + the copy-paste command/config + Server URL + a one-time token.
 *   • Tool-rail (Gmail / Slack / …): a "coming soon" explainer.
 *
 * Reuses the shared MCP access API (global server URL + a single personal token).
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { WebDialog } from '../common/WebDialog.web';
import { useToast } from '../../hooks/useToast';
import { confirm } from '../../lib/alert.web';
import {
  createMcpToken,
  fetchMcpAccessState,
  revokeMcpToken,
  rotateMcpToken,
  type McpTokenSummary,
} from '../../lib/mcp-access-api';
import { BRAND_SVG } from './brand-logos';
import {
  connectorSetupSpec,
  CONSENT_CAN_KEYS,
  CONSENT_CANNOT_KEYS,
  TOKEN_PLACEHOLDER,
} from './connector-setup';
import { TabbioMark } from '@/components/common/TabbioMark';

interface SetupConnector {
  slug: string;
  name: string;
  rail: 'tool' | 'ai' | string;
  status?: string;
  description?: string;
  iconKey?: string | null;
}

interface Props {
  connector: SetupConnector;
  onClose: () => void;
  /** Settings owns the surrounding dialog and scrolling. */
  embedded?: boolean;
}

function activeTokenOf(tokens: McpTokenSummary[]): McpTokenSummary | null {
  return tokens.find((tok) => !tok.revokedAt) ?? null;
}

export function ConnectorSetupDialog({
  connector,
  onClose,
  embedded = false,
}: Props) {
  const { t } = useTranslation('common');
  const toast = useToast();

  const isTool = connector.rail === 'tool';
  const comingSoon = isTool || connector.status === 'coming_soon';
  const spec = connectorSetupSpec(connector.slug);

  const [serverUrl, setServerUrl] = useState('');
  const [token, setToken] = useState<McpTokenSummary | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(!comingSoon);
  const [accessError, setAccessError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [working, setWorking] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  // AI connectors open on a consent screen (matches the reference + native);
  // "Connect" reveals the endpoint/token setup. Tool rail skips straight to
  // the coming-soon explainer.
  const [phase, setPhase] = useState<'consent' | 'setup'>(
    comingSoon ? 'setup' : 'consent',
  );

  useEffect(() => {
    if (comingSoon) return;
    let alive = true;
    setLoading(true);
    setAccessError(false);
    void fetchMcpAccessState()
      .then((state) => {
        if (!alive) return;
        setServerUrl(state.serverUrl);
        setToken(activeTokenOf(state.tokens));
      })
      .catch(() => {
        if (alive) setAccessError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [comingSoon, loadAttempt]);

  const copy = useCallback(
    async (value: string, key: string) => {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(key);
        setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
      } catch {
        toast.error(t('connectors.setup.copyFailed'));
      }
    },
    [toast, t],
  );

  const generate = useCallback(async () => {
    setWorking(true);
    try {
      const result = token
        ? await rotateMcpToken(token.id)
        : await createMcpToken({ includePersonal: true });
      setToken(result.token);
      setSecret(result.secret);
    } catch (error) {
      toast.error(
        t('connectors.setup.tokenFailed'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setWorking(false);
    }
  }, [token, toast, t]);

  const revoke = useCallback(async () => {
    if (!token) return;
    const ok = await confirm({
      title: t('connectors.setup.revokeTitle'),
      message: t('connectors.setup.revokeBody'),
      confirmLabel: t('connectors.setup.revoke'),
      cancelLabel: t('actions.cancel'),
      destructive: true,
    });
    if (!ok) return;
    setWorking(true);
    try {
      await revokeMcpToken(token.id);
      setToken(null);
      setSecret(null);
      toast.success(t('connectors.setup.revoked'));
    } catch (error) {
      toast.error(
        t('connectors.setup.tokenFailed'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setWorking(false);
    }
  }, [token, toast, t]);

  const steps = t(`connectors.setup.${connector.slug}.steps`, {
    returnObjects: true,
    defaultValue: [],
  }) as string[];
  const intro = t(`connectors.setup.${connector.slug}.intro`, {
    defaultValue: t('connectors.setup.introFallback', { name: connector.name }),
  });
  const codeValue = spec?.code
    ? spec.code(serverUrl || 'https://…/api/mcp', secret ?? TOKEN_PLACEHOLDER)
    : null;

  const brand = BRAND_SVG[connector.slug];
  const brandMark =
    brand != null ? (
      <img
        src={`data:image/svg+xml;utf8,${encodeURIComponent(brand)}`}
        width={40}
        height={40}
        alt=""
      />
    ) : (
      <MaterialSymbol
        name={
          connector.iconKey === 'code'
            ? 'code'
            : connector.iconKey === 'rocket'
              ? 'rocket_launch'
              : 'extension'
        }
        size={22}
      />
    );

  // ── Consent phase (AI rail): matches the reference permissions card. ──
  if (phase === 'consent') {
    return (
      <SetupFrame
        embedded={embedded}
        title={t('connectors.connectName', { name: connector.name })}
        onClose={onClose}
        maxWidth={520}
        footer={
          <div
            style={{
              display: 'flex',
              gap: 10,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <button type="button" onClick={onClose} style={secondaryBtnStyle}>
              {t('actions.cancel')}
            </button>
            <button
              type="button"
              onClick={() => setPhase('setup')}
              style={primaryBtnStyle}
            >
              {t('connectors.connect')}
            </button>
          </div>
        }
      >
        {/* Paired Tabbio ↔ provider marks */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            marginBottom: 20,
          }}
        >
          <span style={tabbioMark}>
            <TabbioMark color={'var(--text-primary)'} />
          </span>
          <MaterialSymbol
            name="link"
            size={20}
            style={{ color: 'var(--text-tertiary)' }}
          />
          <span style={brandDisc(!!brand)}>{brandMark}</span>
        </div>

        <div style={permCard}>
          <div style={permLabel}>{t('connectors.consent.willBeAbleTo')}</div>
          {CONSENT_CAN_KEYS.map((key) => (
            <div key={key} style={permRow}>
              <MaterialSymbol
                name="check"
                size={17}
                style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
              />
              <span style={permText}>{t(`connectors.consent.${key}`)}</span>
            </div>
          ))}
          <div style={permDivider} />
          <div style={permLabel}>{t('connectors.consent.willNot')}</div>
          {CONSENT_CANNOT_KEYS.map((key) => (
            <div key={key} style={permRow}>
              <MaterialSymbol
                name="close"
                size={17}
                style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
              />
              <span style={{ ...permText, color: 'var(--text-secondary)' }}>
                {t(`connectors.consent.${key}`)}
              </span>
            </div>
          ))}
        </div>
        <p style={{ ...hint, textAlign: 'center' }}>
          {t('connectors.consent.disconnectAnytime')}
        </p>
      </SetupFrame>
    );
  }

  return (
    <SetupFrame
      embedded={embedded}
      title={connector.name}
      subtitle={
        comingSoon
          ? t('connectors.setup.comingSoonSubtitle')
          : t('connectors.setup.subtitle', { name: connector.name })
      }
      onClose={onClose}
      maxWidth={560}
      footer={
        <div
          style={{
            display: 'flex',
            gap: 10,
            width: '100%',
            alignItems: 'center',
          }}
        >
          {spec?.docUrl ? (
            <a
              href={spec.docUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...secondaryBtnStyle,
                textDecoration: 'none',
                lineHeight: '44px',
              }}
            >
              {t('connectors.setup.openDocs')}
            </a>
          ) : null}
          <button type="button" onClick={onClose} style={primaryBtnStyle}>
            {t('connectors.setup.done')}
          </button>
        </div>
      }
    >
      {/* Brand header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 18,
        }}
      >
        <span style={brandDisc(!!brand)}>{brandMark}</span>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: '20px',
            color: 'var(--text-secondary)',
          }}
          dir="auto"
        >
          {intro}
        </p>
      </div>

      {comingSoon ? (
        <div style={comingSoonBox}>
          <MaterialSymbol
            name="schedule"
            size={22}
            style={{ color: 'var(--text-secondary)' }}
          />
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: 'var(--text-secondary)',
              textAlign: 'center',
            }}
          >
            {t('connectors.setup.comingSoonBody', { name: connector.name })}
          </p>
        </div>
      ) : loading ? (
        <div
          style={{
            padding: '40px 0',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <span style={spinner} />
        </div>
      ) : accessError ? (
        <div role="alert">
          <p style={hint}>{t('connectors.simple.setupFailed')}</p>
          <button
            type="button"
            onClick={() => setLoadAttempt((value) => value + 1)}
            style={{ ...generateBtn, marginTop: 16 }}
          >
            {t('actions.retry')}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {/* Steps */}
          {steps.length ? (
            <section>
              <div style={sectionLabel}>{t('connectors.setup.stepsLabel')}</div>
              <ol
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {steps.map((step, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                    }}
                  >
                    <span style={stepNum}>{i + 1}</span>
                    <span
                      style={{
                        fontSize: 14,
                        lineHeight: '21px',
                        color: 'var(--text-secondary)',
                      }}
                      dir="auto"
                    >
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}

          {/* Code snippet (command / config) */}
          {codeValue ? (
            <section>
              <div style={sectionLabel}>
                {spec?.codeKind === 'config'
                  ? t('connectors.setup.configLabel')
                  : t('connectors.setup.commandLabel')}
              </div>
              <div style={codeBox}>
                <pre style={codePre}>{codeValue}</pre>
                <button
                  type="button"
                  onClick={() => void copy(codeValue, 'code')}
                  style={copyChip}
                  aria-label={t('connectors.setup.copy')}
                >
                  <MaterialSymbol
                    name={copied === 'code' ? 'check' : 'content_copy'}
                    size={15}
                  />
                  {copied === 'code'
                    ? t('connectors.setup.copied')
                    : t('connectors.setup.copy')}
                </button>
              </div>
              {!secret ? (
                <p style={hint}>{t('connectors.setup.needTokenForCode')}</p>
              ) : null}
            </section>
          ) : null}

          {/* Server URL */}
          <section>
            <div style={sectionLabel}>{t('connectors.setup.serverUrl')}</div>
            <CopyField
              value={serverUrl}
              copied={copied === 'url'}
              onCopy={() => void copy(serverUrl, 'url')}
              copyLabel={t('connectors.setup.copy')}
              copiedLabel={t('connectors.setup.copied')}
            />
          </section>

          {/* Token */}
          <section>
            <div style={sectionLabel}>{t('connectors.setup.token')}</div>
            {secret ? (
              <>
                <div style={oneTimeBox}>
                  <MaterialSymbol
                    name="key"
                    size={16}
                    style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
                  />
                  <code
                    style={{
                      flex: 1,
                      fontSize: 13,
                      wordBreak: 'break-all',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {secret}
                  </code>
                  <button
                    type="button"
                    onClick={() => void copy(secret, 'secret')}
                    style={copyChip}
                  >
                    <MaterialSymbol
                      name={copied === 'secret' ? 'check' : 'content_copy'}
                      size={15}
                    />
                    {copied === 'secret'
                      ? t('connectors.setup.copied')
                      : t('connectors.setup.copy')}
                  </button>
                </div>
                <p style={{ ...hint, color: '#b26a00' }}>
                  {t('connectors.setup.oneTimeWarning')}
                </p>
              </>
            ) : token ? (
              <div style={tokenRow}>
                <MaterialSymbol
                  name="check_circle"
                  size={16}
                  style={{ color: 'var(--text-secondary)' }}
                />
                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                  }}
                >
                  {t('connectors.setup.tokenActive')} ·{' '}
                  <code>{token.maskedToken}</code>
                </span>
                <button
                  type="button"
                  onClick={() => void generate()}
                  disabled={working}
                  style={ghostBtn}
                >
                  {t('connectors.setup.regenerate')}
                </button>
                <button
                  type="button"
                  onClick={() => void revoke()}
                  disabled={working}
                  style={{ ...ghostBtn, color: '#d93025' }}
                >
                  {t('connectors.setup.revoke')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => void generate()}
                disabled={working}
                style={generateBtn}
              >
                <MaterialSymbol name="add" size={18} />
                {working
                  ? t('connectors.setup.generating')
                  : t('connectors.setup.generateToken')}
              </button>
            )}
            <p style={hint}>{t('connectors.setup.personalAccess')}</p>
          </section>
        </div>
      )}
    </SetupFrame>
  );
}

function SetupFrame({
  embedded,
  title,
  subtitle,
  onClose,
  maxWidth,
  children,
  footer,
}: {
  embedded: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  maxWidth: number;
  children: ReactNode;
  footer: ReactNode;
}) {
  const { t } = useTranslation('common');
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (embedded) heading.current?.focus();
  }, [embedded, title]);
  if (!embedded)
    return (
      <WebDialog
        title={title}
        subtitle={subtitle}
        onClose={onClose}
        maxWidth={maxWidth}
        footer={footer}
      >
        {children}
      </WebDialog>
    );
  return (
    <section>
      <button
        type="button"
        onClick={onClose}
        style={{
          ...ghostBtn,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 44,
          padding: '8px 0',
          marginBottom: 8,
        }}
      >
        <MaterialSymbol
          name="arrow_back"
          size={18}
          className="tabbio-rtl-flip"
        />
        {t('connectors.simple.back')}
      </button>
      <h3
        ref={heading}
        tabIndex={-1}
        style={{
          margin: '0 0 16px',
          fontSize: 22,
          fontWeight: 400,
          outline: 'none',
        }}
      >
        {title}
      </h3>
      {subtitle ? (
        <p style={{ ...hint, marginBottom: 20 }}>{subtitle}</p>
      ) : null}
      {children}
      <div
        style={{
          borderTop: '1px solid var(--input-border)',
          paddingTop: 20,
          marginTop: 24,
        }}
      >
        {footer}
      </div>
    </section>
  );
}

function CopyField({
  value,
  onCopy,
  copied,
  copyLabel,
  copiedLabel,
}: {
  value: string;
  onCopy: () => void;
  copied: boolean;
  copyLabel: string;
  copiedLabel: string;
}) {
  return (
    <div style={tokenRow}>
      <code
        style={{
          flex: 1,
          fontSize: 13,
          wordBreak: 'break-all',
          color: 'var(--text-primary)',
        }}
      >
        {value || 'N/A'}
      </code>
      <button type="button" onClick={onCopy} style={copyChip}>
        <MaterialSymbol name={copied ? 'check' : 'content_copy'} size={15} />
        {copied ? copiedLabel : copyLabel}
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const brandDisc = (hasBrand: boolean): CSSProperties => ({
  width: 48,
  height: 48,
  borderRadius: hasBrand ? 12 : '50%',
  background: hasBrand ? 'transparent' : 'rgba(0,0,0,0.06)',
  border: hasBrand ? '1px solid rgba(0,0,0,0.06)' : 'none',
  color: 'var(--text-secondary)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  overflow: 'hidden',
});

const sectionLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text-tertiary)',
  marginBottom: 10,
};

const tabbioMark: CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: 14,
  // background: 'color-mix(in srgb, var(--gem-accent) 12%, transparent)',
  color: 'var(--gem-accent)',
  fontSize: 18,
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const permCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 11,
  padding: '16px 18px',
  borderRadius: 16,
  background: 'var(--hover)',
};

const permLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
};

const permRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const permText: CSSProperties = {
  fontSize: 14,
  color: 'var(--text-primary)',
};

const permDivider: CSSProperties = {
  height: 1,
  background: 'var(--input-border)',
};

const stepNum: CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.06)',
  color: 'var(--text-secondary)',
  fontSize: 12,
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
};

const codeBox: CSSProperties = {
  position: 'relative',
  borderRadius: 14,
  background: 'rgba(0,0,0,0.05)',
  padding: '14px 14px 14px',
};

const codePre: CSSProperties = {
  margin: 0,
  paddingInlineEnd: 70,
  fontSize: 12.5,
  lineHeight: '19px',
  fontFamily: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
  color: 'var(--text-primary)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};

const copyChip: CSSProperties = {
  position: 'absolute',
  top: 10,
  insetInlineEnd: 10,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  height: 28,
  padding: '0 10px',
  borderRadius: 8,
  border: '1px solid var(--input-border)',
  background: 'var(--bg)',
  color: 'var(--text-secondary)',
  fontSize: 12,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const tokenRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minHeight: 48,
  padding: '8px 12px',
  borderRadius: 14,
  border: '1px solid var(--input-border)',
  background: 'var(--bg)',
  position: 'relative',
};

const oneTimeBox: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  borderRadius: 14,
  background: 'rgba(0,0,0,0.05)',
  position: 'relative',
};

const generateBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 46,
  padding: '0 18px',
  borderRadius: 9999,
  border: 0,
  background: 'var(--text-primary)',
  color: 'var(--bg)',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const ghostBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--text-secondary)',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
  padding: '4px 6px',
};

const hint: CSSProperties = {
  margin: '8px 2px 0',
  fontSize: 12,
  lineHeight: '17px',
  color: 'var(--text-tertiary)',
};

const comingSoonBox: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  padding: '32px 20px',
  borderRadius: 16,
  background: 'var(--hover)',
};

const spinner: CSSProperties = {
  width: 24,
  height: 24,
  border: '2px solid rgba(0,0,0,0.12)',
  borderTopColor: 'var(--text-secondary)',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};

const primaryBtnStyle: CSSProperties = {
  flex: 1,
  height: 46,
  borderRadius: 9999,
  border: 0,
  backgroundColor: 'var(--text-primary)',
  color: 'var(--bg)',
  fontSize: 15,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const secondaryBtnStyle: CSSProperties = {
  flex: 1,
  height: 46,
  borderRadius: 9999,
  border: '1px solid var(--input-border)',
  background: 'transparent',
  color: 'var(--text-primary)',
  fontSize: 15,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
  textAlign: 'center',
};
